'use client';

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useParams } from 'react-router-dom';

export default function Information() {
  const [activeSection, setActiveSection] = useState('about');
  const location = useLocation();
  const params = useParams();
  
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const querySection = queryParams.get('section');
    const pathSection = params.section;
    
    // First check URL path parameter, then query parameter
    if (pathSection && sections[pathSection]) {
      setActiveSection(pathSection);
    } else if (querySection && sections[querySection]) {
      setActiveSection(querySection);
    }
  }, [location, params]);

  const sections = {
    // Company section
    about: {
      title: 'About Us',
      content: `WizardPortrait brings the magic of wizarding portraits to the muggle world. Founded in 2025, 
      our mission is to combine cutting-edge AI technology with the wonder of living art to create 
      enchanted companions that feel truly magical. We're a team of technologists, artists, and 
      storytellers working to blur the line between technology and magic.`
    },
    team: {
      title: 'Our Team',
      content: `Our team consists of passionate individuals from diverse backgrounds, all united by a love 
      for innovation and storytelling. From AI researchers to character designers, each member brings 
      unique skills that make WizardPortrait possible.
      
      Leadership:
      • Aurora Villanova Blackwood - Founder & CEO
      • Merlin Thatcher - Chief Technology Officer
      • Ophelia Winterborn - Creative Director
      • Eldon Ravenscroft - Head of AI Research`
    },
    careers: {
      title: 'Careers',
      content: `Join our magical team! We're always looking for talented individuals who share our passion 
      for blending technology and magic. We offer competitive compensation, flexible work arrangements, 
      and the chance to work on something truly unique.
      
      Current Openings:
      • AI Research Engineer
      • Character Artist
      • Front-end Developer
      • Customer Experience Wizard
      
      Apply by sending your resume to careers@wizardportrait.com`
    },
    
    
    // Support section
    help: {
      title: 'Help Center',
      content: `Our comprehensive Help Center contains guides, tutorials, and troubleshooting steps for common 
      questions about your WizardPortrait experience.
      
      Popular Topics:
      • Getting Started with Your Portrait
      • Voice Training Tips
      • Customizing Your Portrait's Personality
      • Troubleshooting Connection Issues`
    },
    contact: {
      title: 'Contact Us',
      content: `We're here to assist you with any questions or concerns.
      
      Email: support@wizardportrait.com
      Hours: Monday-Friday, 9am-6pm EST
      
      For urgent matters, please reach out to us on intagram or tiktok.`
    },
    faq: {
      title: 'FAQ',
      content: `Frequently Asked Questions
      
      Q: How does my portrait respond to me?
      A: Your portrait uses advanced AI to understand speech and respond naturally using both voice and animations.
      
      Q: Can I change my portrait's appearance after creation?
      A: Yes! You can modify various aspects of your portrait through the customization menu.
      
      Q: Does my portrait require an internet connection?
      A: Yes, your portrait needs an internet connection to process conversations and update its behaviors.
      
      Q: How secure is my data?
      A: We use industry-standard encryption and never share your personal conversations with third parties.`
    },
    community: {
      title: 'Community',
      content: `Join our vibrant community of portrait owners to share experiences, tips, and stories!
      
      • Forums: Discuss portrait customization, share stories, and get help
      • Events: Join our monthly virtual meetups
      • Portrait Gallery: Share your portrait creations
      • Beta Program: Test new features before they're released
      
      Visit community.wizardportrait.com to join the conversation.`
    },
    
    // Legal section
    terms: {
      title: 'Terms of Service',
      content: `These Terms of Service ("Terms") govern your access to and use of WizardPortrait services. 
      By using our services, you agree to be bound by these Terms.
      
      1. Account Terms
      You are responsible for maintaining the security of your account. You are responsible for all activities 
      that occur under your account.
      
      2. Acceptable Use
      You may not use WizardPortrait for any illegal purposes or to violate the rights of others.
      
      3. Content Ownership
      We do not claim ownership of your content, but you grant us a license to use it to provide our services.
      
      4. Termination
      We reserve the right to terminate or suspend your account for violations of these Terms.
      
      Last updated: January 1, 2025`
    },
    privacy: {
      title: 'Privacy Policy',
      content: `This Privacy Policy describes how WizardPortrait collects, uses, and shares your personal information.
      
      1. Information We Collect
      We collect information you provide directly, such as your name, email, and portrait preferences.
      We also collect usage data and technical information about your device.
      
      2. How We Use Your Information
      We use your information to provide and improve our services, process transactions, 
      and communicate with you.
      
      3. Information Sharing
      We do not sell your personal information. We may share information with service providers,
      for legal reasons, or in case of business transfers.
      
      4. Your Rights
      Depending on your location, you may have rights to access, correct, or delete your information.
      
      Last updated: January 1, 2025`
    },
    cookie: {
      title: 'Cookie Policy',
      content: `WizardPortrait uses cookies and similar technologies to enhance your experience on our website.
      
      1. What Are Cookies
      Cookies are small text files stored on your device that help websites remember your preferences.
      
      2. How We Use Cookies
      We use cookies for essential functions, analytics, personalization, and marketing purposes.
      
      3. Managing Cookies
      You can manage or delete cookies through your browser settings. However, disabling certain 
      cookies may affect functionality.
      
      4. Updates to This Policy
      We may update this Cookie Policy to reflect changes in our practices or for legal reasons.
      
      Last updated: January 1, 2025`
    }
  };

  return (
    <div className="information-page">
      <div className="info-header">
        <h1>Information Hub</h1>
        <p>Everything you need to know about WizardPortrait</p>
      </div>

      <div className="info-content">
        <div className="info-sidebar">
          <div className="info-section">
            <h3>Company</h3>
            <ul>
              <li className={activeSection === 'about' ? 'active' : ''} onClick={() => setActiveSection('about')}>About Us</li>
              <li className={activeSection === 'team' ? 'active' : ''} onClick={() => setActiveSection('team')}>Our Team</li>
              <li className={activeSection === 'careers' ? 'active' : ''} onClick={() => setActiveSection('careers')}>Careers</li>
            </ul>
          </div>
          
          <div className="info-section">
            <h3>Support</h3>
            <ul>
              <li className={activeSection === 'help' ? 'active' : ''} onClick={() => setActiveSection('help')}>Help Center</li>
              <li className={activeSection === 'contact' ? 'active' : ''} onClick={() => setActiveSection('contact')}>Contact Us</li>
              <li className={activeSection === 'faq' ? 'active' : ''} onClick={() => setActiveSection('faq')}>FAQ</li>
              <li className={activeSection === 'community' ? 'active' : ''} onClick={() => setActiveSection('community')}>Community</li>
            </ul>
          </div>
          
          <div className="info-section">
            <h3>Legal</h3>
            <ul>
              <li className={activeSection === 'terms' ? 'active' : ''} onClick={() => setActiveSection('terms')}>Terms of Service</li>
              <li className={activeSection === 'privacy' ? 'active' : ''} onClick={() => setActiveSection('privacy')}>Privacy Policy</li>
              <li className={activeSection === 'cookie' ? 'active' : ''} onClick={() => setActiveSection('cookie')}>Cookie Policy</li>
            </ul>
          </div>
        </div>
        
        <motion.div 
          className="info-details"
          key={activeSection}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2>{sections[activeSection].title}</h2>
          <div className="info-content-text">
            {sections[activeSection].content.split('\n').map((paragraph, idx) => (
              <p key={idx}>{paragraph}</p>
            ))}
          </div>
        </motion.div>
      </div>

      <style jsx>{`
        .information-page {
          min-height: 100vh;
          background: linear-gradient(to bottom, #030304, #0d0d12);
          color: #e0e0e0;
          padding: 6rem 2rem;
        }
        
        .info-header {
          text-align: center;
          margin-bottom: 3rem;
          max-width: 800px;
          margin: 0 auto 4rem;
        }
        
        .info-header h1 {
          font-family: serif;
          font-size: 3rem;
          color: #f7c94b;
          margin-bottom: 1rem;
          font-weight: 400;
        }
        
        .info-header p {
          font-size: 1.2rem;
          opacity: 0.8;
        }
        
        .info-content {
          display: flex;
          max-width: 1200px;
          margin: 0 auto;
          gap: 3rem;
        }
        
        .info-sidebar {
          flex: 0 0 250px;
          border-right: 1px solid rgba(255, 255, 255, 0.1);
          padding-right: 2rem;
        }
        
        .info-section {
          margin-bottom: 2rem;
        }
        
        .info-section h3 {
          font-family: serif;
          color: #f7c94b;
          font-size: 1.4rem;
          margin-bottom: 1rem;
        }
        
        .info-section ul {
          list-style: none;
          padding: 0;
        }
        
        .info-section li {
          margin-bottom: 0.8rem;
          padding: 0.5rem 0;
          cursor: pointer;
          transition: all 0.3s ease;
          opacity: 0.7;
        }
        
        .info-section li:hover {
          opacity: 1;
          padding-left: 0.5rem;
          color: #f7c94b;
        }
        
        .info-section li.active {
          opacity: 1;
          padding-left: 0.5rem;
          color: #f7c94b;
          border-left: 2px solid #f7c94b;
        }
        
        .info-details {
          flex: 1;
          padding: 1rem;
        }
        
        .info-details h2 {
          font-family: serif;
          color: #f7c94b;
          font-size: 2.2rem;
          margin-bottom: 2rem;
          font-weight: 400;
          border-bottom: 1px solid rgba(247, 201, 75, 0.3);
          padding-bottom: 1rem;
        }
        
        .info-content-text p {
          margin-bottom: 1.5rem;
          font-size: 1.1rem;
          line-height: 1.7;
        }
        
        @media (max-width: 992px) {
          .info-content {
            flex-direction: column;
          }
          
          .info-sidebar {
            flex: auto;
            border-right: none;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            padding-right: 0;
            padding-bottom: 2rem;
            margin-bottom: 2rem;
          }
          
          .info-section {
            margin-bottom: 1.5rem;
          }
          
          .info-section h3 {
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          
          .info-section h3:after {
            content: '+';
            font-size: 1.2rem;
          }
        }
      `}</style>
    </div>
  );
} 